import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

export const verificationAPI = {
  sendWhatsApp: (phone_number) => api.post('/verification/send-whatsapp', { phone_number }),
  verifyWhatsApp: (phone_number, code) => api.post('/verification/verify-whatsapp', { phone_number, code }),
  verifyEmail: (token) => api.post('/verification/verify-email', { token }),
  verifyFace: (user_id, face_data, id_document, id_type, id_document_back = null) => 
    api.post('/verification/verify-face', { user_id, face_data, id_document, id_type, id_document_back }),
  verifyBusiness: (document_type, document_data, registration_number, established_date, expiry_date, additionalData = {}) => 
    api.post('/verification/verify-business', { 
      document_type, 
      document_data, 
      registration_number, 
      established_date, 
      expiry_date,
      business_name: additionalData.businessName,
      business_email: additionalData.businessEmail,
      business_phone: additionalData.businessPhone,
      country: additionalData.country,
      address: additionalData.address,
      city: additionalData.city,
      postal_code: additionalData.postalCode,
      tax_number: additionalData.taxNumber,
      tax_document: additionalData.taxDocument,
      email_verified: additionalData.emailVerified
    }),
  sendBusinessEmailCode: (email) => api.post('/verification/send-business-email-code', { email }),
  verifyBusinessEmailCode: (email, code) => api.post('/verification/verify-business-email-code', { email, code }),
};

export const subscriptionAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getStatus: () => api.get('/subscriptions/status'),
};

export const walletAPI = {
  getWallet: () => api.get('/wallet'),
};

export const quoteAPI = {
  getRates: (data) => api.post('/quotes/get-rates', data),
  createQuote: (data) => api.post('/quotes/create', data),
  getQuotes: () => api.get('/quotes'),
  getQuote: (id) => api.get(`/quotes/${id}`),
  downloadPDF: (id) => `${API_BASE}/quotes/${id}/pdf`,
};

export const paymentAPI = {
  createCheckout: (package_id, origin_url) => api.post('/payments/stripe/checkout', { package_id, origin_url }),
  getPaymentStatus: (session_id) => api.get(`/payments/stripe/status/${session_id}`),
};

export const adminAPI = {
  getAnalytics: () => api.get('/admin/analytics'),
  getUsers: () => api.get('/admin/users'),
  getVerifications: () => api.get('/admin/verifications'),
  getPendingVerifications: () => api.get('/admin/verifications/pending'),
  decideFaceVerification: (verification_id, decision, reason) => 
    api.post('/admin/verifications/face/decide', { verification_id, decision, reason }),
  decideBusinessVerification: (verification_id, decision, reason) => 
    api.post('/admin/verifications/business/decide', { verification_id, decision, reason }),
  createUser: (userData) => api.post('/admin/users/create', userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

export const productAPI = {
  getCategories: () => api.get('/products/categories'),
  createProduct: (data) => api.post('/products/create', data),
  listProducts: (params) => api.get('/products', { params }),
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/products/${id}`),
};